import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Lock, Eye, EyeOff, CheckCircle, Check } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [validSession, setValidSession] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);

  // Check if we have a valid recovery session
  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        // Check if this is a recovery session (user clicked reset link in email)
        if (session) {
          setValidSession(true);
        } else {
          // Listen for the SIGNED_IN event which happens when user clicks the reset link
          const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
            if (event === 'PASSWORD_RECOVERY' || (event === 'SIGNED_IN' && session)) {
              setValidSession(true);
            }
          });
          
          // Give it a moment for the auth state to update
          setTimeout(() => {
            setCheckingSession(false);
          }, 1000);
          
          return () => subscription?.unsubscribe();
        }
      } catch (error) {
        console.error('Error checking session:', error);
      } finally {
        setCheckingSession(false);
      }
    };

    checkSession();
  }, []);

  const validatePassword = (password) => {
    const checks = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password)
    };
    return checks;
  };

  const passwordChecks = validatePassword(password);
  const isPasswordValid = Object.values(passwordChecks).every(Boolean);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (!isPasswordValid) {
      toast.error('Password does not meet requirements');
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) {
        toast.error(error.message);
      } else {
        setSuccess(true);
        toast.success('Password updated successfully!');
        
        // Sign out and redirect to login after a short delay
        setTimeout(async () => {
          await supabase.auth.signOut();
          navigate('/login');
        }, 2000);
      }
    } catch (error) {
      toast.error('Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  const PasswordCheck = ({ passed, text }) => (
    <div className={`flex items-center gap-2 text-xs ${passed ? 'text-green-600' : 'text-[#737373]'}`}>
      <div className={`w-4 h-4 rounded-full flex items-center justify-center ${passed ? 'bg-green-100' : 'bg-[#F5F5F5]'}`}>
        {passed && <Check className="w-3 h-3" />}
      </div>
      {text}
    </div>
  );

  // Loading state
  if (checkingSession) {
    return (
      <div className="min-h-screen pt-28 pb-16 flex items-center justify-center px-6">
        <div className="animate-pulse text-[#737373]">Verifying reset link...</div>
      </div>
    );
  }

  // Invalid or expired link
  if (!validSession && !checkingSession) {
    return (
      <div className="min-h-screen pt-28 pb-16 flex items-center justify-center px-6" data-testid="reset-password-invalid">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Lock className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="font-serif text-2xl font-semibold text-[#0A0A0A] mb-4">
            Invalid or Expired Link
          </h1>
          <p className="text-[#737373] mb-8">
            This password reset link is invalid or has expired. Please request a new password reset link.
          </p>
          <Button
            asChild
            className="bg-[#0A0A0A] text-white hover:bg-[#262626] rounded-none h-12 px-8"
          >
            <Link to="/forgot-password">Request New Link</Link>
          </Button>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen pt-28 pb-16 flex items-center justify-center px-6" data-testid="reset-password-success">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="font-serif text-2xl font-semibold text-[#0A0A0A] mb-4">
            Password Updated!
          </h1>
          <p className="text-[#737373] mb-8">
            Your password has been successfully updated. Redirecting you to login...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-28 pb-16 flex items-center justify-center px-6" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        {/* Back Link */}
        <Link 
          to="/login"
          className="inline-flex items-center gap-2 text-sm text-[#737373] hover:text-[#0A0A0A] transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to login
        </Link>

        {/* Header */}
        <div className="mb-8">
          <h1 className="font-serif text-3xl font-semibold text-[#0A0A0A] mb-2">
            Reset Your Password
          </h1>
          <p className="text-[#737373]">
            Enter your new password below.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="password">New Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#737373]" />
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter new password"
                className="pl-10 pr-10 rounded-none h-12 border-[#E5E5E5] focus:border-[#0A0A0A] focus:ring-0"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#737373] hover:text-[#0A0A0A]"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            
            {/* Password Requirements */}
            {password && (
              <div className="mt-3 p-3 bg-[#FAFAFA] border border-[#E5E5E5] space-y-2">
                <p className="text-xs font-medium text-[#0A0A0A] mb-2">Password must have:</p>
                <PasswordCheck passed={passwordChecks.length} text="At least 8 characters" />
                <PasswordCheck passed={passwordChecks.uppercase} text="One uppercase letter" />
                <PasswordCheck passed={passwordChecks.lowercase} text="One lowercase letter" />
                <PasswordCheck passed={passwordChecks.number} text="One number" />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm New Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#737373]" />
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                className="pl-10 pr-10 rounded-none h-12 border-[#E5E5E5] focus:border-[#0A0A0A] focus:ring-0"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#737373] hover:text-[#0A0A0A]"
              >
                {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {confirmPassword && password !== confirmPassword && (
              <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
            )}
          </div>

          <Button
            type="submit"
            disabled={loading || !isPasswordValid || password !== confirmPassword}
            className="w-full bg-[#0A0A0A] text-white hover:bg-[#262626] rounded-none h-12 disabled:opacity-50"
          >
            {loading ? 'Updating...' : 'Update Password'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
